// The two pieces of behaviour on the course finder: the search combobox, and
// the institution → programme narrowing in the picker beneath it.
//
// NOT IN packs/. shakapacker.yml sets source_entry_path: packs with
// nested_entries: true, so every file under that directory becomes its own
// webpack entrypoint and emits a bundle no layout ever loads. helpers.js is the
// existing precedent for a plain module imported by packs/application.js.
//
// No framework. This app has neither Turbo nor Stimulus, deliberately, and a
// finder is not the place to introduce one. Both functions early-return when
// their markup is absent, so this costs nothing on the other ~200 pages the
// pack is emitted on.
//
// THE ONLY STRINGS WRITTEN HERE ARE ABOUT THE SEARCH ITSELF — that nothing
// matched, or that the search is down. Everything a row says about a COURSE is
// rendered by CoursesController#search: the dropdown states a price and whether
// a course is open, and this app has been wrong about both before by restating
// them away from the code that decides them.

const DEBOUNCE_MS = 250;

// Text only, never innerHTML. A course title is admin-entered rather than
// student-entered, but this renders whatever the endpoint returns and the only
// safe assumption about that is none.
function cell(className, text) {
  const node = document.createElement("span");
  node.className = className;
  node.textContent = text;
  return node;
}

// One call per search box. The public layout carries a second copy in the
// finder dialog (layouts/_finder_dialog), so a page may hold two; each keeps
// its own state and its own ids, read off the markup the partial rendered.
function setupSearch(root) {
  const input = root.querySelector(".course-search__input");
  const list = root.querySelector(".course-search__list");
  const status = root.querySelector(".course-search__status");
  if (!input || !list || !status) return;

  // Row ids hang off the list's own id, which courses/_course_search prefixes
  // per copy — two lists writing "course-search-option-0" would send
  // aria-activedescendant to the wrong row.
  const optionPrefix = `${list.id}-option`;
  const endpoint = root.dataset.endpoint;
  const askUrl = root.dataset.askUrl;
  const askLabel = root.dataset.askLabel;
  // Set on the home page's box only. A pick from there carries from=home so
  // CoursesController#show can count it as the funnel's resolve; read off a
  // URL param, never off the referer.
  const source = root.dataset.source;
  const minimum = parseInt(root.dataset.minimum, 10) || 2;

  let timer = null;
  let active = -1;
  let sequence = 0;

  // The home page's hero (pages/_welcome, data-search-stage). While the box
  // holds text it is .is-searching: the intro folds away so the answer is not
  // pushed off the screen, and on a phone the box goes to the top, above the
  // keyboard. Every other copy of the box has no stage and skips this.
  const stage = root.closest("[data-search-stage]");
  const phone = window.matchMedia("(max-width: 575.98px)");
  const still = window.matchMedia("(prefers-reduced-motion: reduce)");

  // The scroll waits for the fold: measured while the intro is still
  // standing, the box's position is out by the intro's height and the scroll
  // overshoots it off the top. The timer stands in for a transitionend that
  // never fires (reduced motion, or the fold already done).
  function liftBox() {
    if (!phone.matches || !stage.classList.contains("is-searching")) return;
    root.scrollIntoView({ block: "start", behavior: still.matches ? "auto" : "smooth" });
  }

  function stageSearch(on) {
    if (!stage || stage.classList.contains("is-searching") === on) return;
    stage.classList.toggle("is-searching", on);
    if (!on || !phone.matches) return;

    const intro = stage.querySelector(".home-hero__intro");
    let lifted = false;
    const lift = () => {
      if (lifted) return;
      lifted = true;
      liftBox();
    };
    // The opacity fade ends first; only the height's end means the box has
    // stopped moving.
    const onEnd = (event) => {
      if (event.propertyName !== "grid-template-rows") return;
      intro.removeEventListener("transitionend", onEnd);
      lift();
    };
    intro?.addEventListener("transitionend", onEnd);
    window.setTimeout(lift, still.matches ? 0 : 320);
  }

  function close() {
    list.hidden = true;
    list.replaceChildren();
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    active = -1;
  }

  function rows() {
    return Array.from(list.querySelectorAll(".course-search__option"));
  }

  // A line inside the overlay rather than under the field. As a paragraph in
  // the flow it pushed the dropdown down the page every time it changed, which
  // moved the first row out from under the pointer on its way to it.
  function noteRow(text) {
    const note = document.createElement("li");
    note.className = "course-search__note";
    note.setAttribute("role", "presentation");
    note.textContent = text;
    return note;
  }

  function highlight(index) {
    const items = rows();
    if (!items.length) return;

    active = (index + items.length) % items.length;
    items.forEach((item, i) => {
      item.classList.toggle("is-active", i === active);
      item.setAttribute("aria-selected", i === active ? "true" : "false");
    });
    input.setAttribute("aria-activedescendant", items[active].id);
    items[active].scrollIntoView({ block: "nearest" });
  }

  function tagged(url) {
    if (!source || !url) return url;
    const target = new URL(url, window.location.origin);
    target.searchParams.set("from", source);
    return target.pathname + target.search + target.hash;
  }

  function go(url) {
    if (url) window.location.assign(url);
  }

  function optionNode(id, url) {
    const option = document.createElement("li");
    option.className = "course-search__option";
    option.id = id;
    // setAttribute rather than the IDL property: ARIA reflection landed in
    // Safari 16.4, and this has to work on the phone browsers students have.
    option.setAttribute("role", "option");
    option.setAttribute("aria-selected", "false");
    option.dataset.url = url;
    // mousedown, not click: the pointer going down blurs the input, and a blur
    // that closes the list would take the row out from under the click.
    // preventDefault stops that blur, which is what keeps the row there long
    // enough to be chosen.
    option.addEventListener("mousedown", (event) => {
      event.preventDefault();
      go(url);
    });
    return option;
  }

  // The ask is a row of the listbox rather than a note under it. A keyboard
  // user who typed a code we do not carry can only reach it by arrowing, and
  // they are precisely who it is for.
  function askRow(index) {
    const option = optionNode(`${optionPrefix}-${index}`, askUrl);
    option.classList.add("course-search__ask");
    option.appendChild(cell("course-search__code", askLabel));
    return option;
  }

  function render(results) {
    list.replaceChildren();

    results.forEach((course, index) => {
      const option = optionNode(`${optionPrefix}-${index}`, tagged(course.url));
      option.appendChild(cell("course-search__code", course.code));
      option.appendChild(cell("course-search__name", course.title));
      option.appendChild(cell("course-search__meta", course.meta));
      option.appendChild(cell("course-search__aside", course.aside));
      list.appendChild(option);
    });

    if (!results.length) list.appendChild(noteRow("No course matches that yet"));
    list.appendChild(askRow(results.length));
    open();

    // Announced, not shown: the rows above say the same thing to anyone who can
    // see them, so this is the screen-reader half of the same answer.
    status.textContent = results.length
      ? `${results.length} matching ${results.length === 1 ? "course" : "courses"}`
      : "No course matches that yet";
  }

  function open() {
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    active = -1;
  }

  function search(query) {
    // Each response carries the number of the request that asked for it, so a
    // slow answer to "ph" cannot land on top of a fast answer to "phar".
    const ticket = ++sequence;

    fetch(`${endpoint}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" }
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((results) => {
        if (ticket === sequence) render(results);
      })
      .catch(() => {
        if (ticket !== sequence) return;
        // The steps below this box are a working way in, so the failure says so
        // rather than leaving a box that has stopped answering. On the home page
        // they are folded into "Browse by programme instead", so it is opened:
        // the message must not point at something the visitor cannot see.
        const browse = root.closest(".course-finder")?.querySelector(".course-finder__browse");
        if (browse) browse.open = true;
        const message = "Search is unavailable right now — use the steps below.";
        list.replaceChildren(noteRow(message), askRow(0));
        open();
        status.textContent = message;
      });
  }

  input.addEventListener("input", () => {
    const query = input.value.trim();
    window.clearTimeout(timer);
    stageSearch(query.length > 0);

    if (query.length < minimum) {
      close();
      status.textContent = "";
      return;
    }

    timer = window.setTimeout(() => search(query), DEBOUNCE_MS);
  });

  input.addEventListener("keydown", (event) => {
    // Only ever swallow a key the open list is actually going to act on.
    // Blanket preventDefault on Enter is how the sibling autocomplete in this
    // app stops its own form from submitting.
    if (list.hidden) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        highlight(active + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        highlight(active - 1);
        break;
      case "Enter":
        if (active < 0) break;
        event.preventDefault();
        go(rows()[active].dataset.url);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      default:
        break;
    }
  });

  document.addEventListener("click", (event) => {
    if (!root.contains(event.target)) close();
  });

  // Tabbing out leaves no click for the handler above to see. Safe to close on
  // blur outright because choosing a row happens on mousedown with the default
  // prevented, so picking one never blurs the input in the first place.
  input.addEventListener("blur", close);
}

function setupCascade() {
  const form = document.querySelector(".course-picker");
  if (!form) return;

  const institution = form.querySelector("#institution_id");
  const programme = form.querySelector("#programme_id");
  const go = form.querySelector(".course-picker__go");
  if (!institution || !programme) return;

  // The steps BELOW the programme are however many the page carries. /courses
  // asks year and semester as well; /bundles stops at the programme, because
  // index_course_bundles_on_slice is unique below it and one more answer would
  // resolve to a single row. Everything above is identical on both, which is
  // why courses/_programme_steps is one partial — and why this function reads
  // the tail rather than requiring it.
  const rest = [form.querySelector("#year_of_study"),
                form.querySelector("#semester")].filter(Boolean);

  // Whole optgroup NODES, held out of the document and put back on demand.
  // Hiding is not an option: `display: none` and `hidden` on an <option> are
  // ignored by Safari and by every mobile native picker, so a narrowed select
  // would still list the whole catalog for a large share of students.
  const groups = Array.from(programme.querySelectorAll("optgroup"));

  function narrow() {
    // Read BEFORE the DOM moves. Removing the group that holds the selected
    // option resets the select to its blank, and re-selecting afterwards is
    // what stops a returning student's remembered programme being cleared —
    // and then written back over their profile on the next submit.
    const chosen = programme.value;
    const wanted = institution.value;

    groups.forEach((group) => group.remove());
    groups
      .filter((group) => !wanted || group.dataset.institution === wanted)
      .forEach((group) => programme.appendChild(group));

    programme.value = chosen;
    // The remembered programme belongs to another institution, so the select
    // now has nothing to show for it.
    if (programme.value !== chosen) programme.value = "";
  }

  // A step is answered, or it is waiting on the one before it. Nothing is
  // disabled server-side, so with this script absent every step submits.
  function gate() {
    programme.disabled = !institution.value;
    rest.forEach((control) => {
      control.disabled = !programme.value;
    });

    [institution, programme, ...rest].forEach((control) => {
      control.closest(".course-picker__step")
             .classList.toggle("is-filled", Boolean(control.value));
    });

    // Submitting a half-answered picker reloads the same page with the same
    // question on it, which reads as a broken button rather than as a missing
    // answer. The server never renders this disabled, so the form still
    // submits with the script absent.
    if (go) {
      go.disabled = !(programme.value && rest.every((control) => control.value));
    }
  }

  institution.addEventListener("change", () => {
    narrow();
    gate();
  });
  [programme, ...rest].forEach((control) =>
    control.addEventListener("change", gate)
  );

  narrow();
  gate();
}

function start() {
  document.querySelectorAll("[data-course-search]").forEach(setupSearch);
  setupCascade();
}

// javascript_pack_tag is deferred (Shakapacker 7 defaults defer: true), so
// DOMContentLoaded may have fired before this module is evaluated — a bare
// listener would never run. This is the guard packs/application.js already uses
// for the same reason.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
